import type { AriaRole, KeyboardEvent, KeyboardEventHandler, SyntheticEvent } from 'react';

export type ArrowDirectionEnum = 'ArrowUp' | 'ArrowDown' | 'ArrowLeft' | 'ArrowRight';

export type AccessibilityActions = {
  onClick?: (event?: SyntheticEvent) => void;
  onTab?: (forward: boolean) => void;
  onEsc?: () => void;
  onArrowNavigation?: (direction: ArrowDirectionEnum) => void;
};

export type AccessibilityProps = {
  tabIndex?: number;
  role?: AriaRole;
  onKeyDown?: KeyboardEventHandler<unknown>;
};

/** Executes the provided function with setTimeout with 0 delay */
export const runInNextCycle = (func: (() => void)): void => {
  setTimeout(func, 0);
};

/**
 * Generates accessibility properties for clickable elements.
 * @param {boolean} [addIndex] -
 * @param {AriaRole} [role='button'] - ARIA role attribute to be assigned to the element.
 * @returns {AccessibilityProps}
 */
export const getClickAccessibilityProps = (params: {
  /** Object containing action handlers for various key events. */
  actions: AccessibilityActions;
  /** If true, sets the tabIndex to 0 to make the element focusable. */
  addIndex?: boolean;
  /** Accessibility properties including ARIA role and key event handlers. */
  role?: AriaRole;
}): AccessibilityProps => {
  const { actions, addIndex, role } = params;
  // Initialize the element properties with default role 'button' or provided role
  const elementProps: AccessibilityProps = {
    role: role || 'button',
    onKeyDown: (event: KeyboardEvent) => {
      // Handle different key events
      switch (event.key) {
        case 'Enter':
        case ' ':
        case 'Spacebar':
          event.preventDefault(); // Prevent default action for Enter and Space keys
          console.log('here in keydown handler for accessibility', actions);

          // Trigger onClick action if defined
          if (actions.onClick) {
            actions.onClick(event);
          }
          break;
        case 'Escape':
          // Trigger onEsc action if defined
          if (actions.onEsc) {
            actions.onEsc();
          }
          break;
        case 'Tab':
          // Trigger onTab action if defined, passing a boolean indicating the direction of tabbing
          if (actions.onTab) {
            actions.onTab(!event.shiftKey);
          }
          break;
        case 'ArrowUp':
        case 'ArrowDown':
          if (actions.onArrowNavigation) {
            actions.onArrowNavigation(event.key);
          }
          break;
      }
    },
  };

  // Add tabIndex property if addIndex is true to make the element focusable
  if (addIndex) {
    elementProps.tabIndex = 0;
  }

  // Return the constructed accessibility properties
  return elementProps;
};

/**
 * Handles arrow key navigation for category elements.
 *
 * This function allows users to navigate through keys.
 * It focuses on the next or previous element and optionally triggers a callback.
 *
 * @param {boolean} forward - Whether to move forward (true) or backward (false) in the tab order.
 * @param {number} index - The current index of the focused element.
 * @param {HTMLAnchorElement[]} arrayOfRefs - Array of element references to navigate through.
 * @param {(nextIndex: number) => void} [selectFunction] - Optional callback to handle selection.
 */
export const handleKeyboardNavigation = (
  forward: boolean,
  index: number,
  arrayOfRefs: HTMLAnchorElement[] | HTMLButtonElement[] | HTMLElement[],
  selectFunction?: (nextIndex: number) => void,
  shouldFocusNextElement: boolean = true,
) => {
  // Guard clause: Return if refs array is invalid
  if (!arrayOfRefs || !Array.isArray(arrayOfRefs)) {
    return;
  }

  // Determine the next index based on navigation direction
  const nextIndex = forward ? Math.min(index + 1, arrayOfRefs.length - 1) : Math.max(index - 1, 0);

  runInNextCycle(() => {
    // Focus the next element in the refs array
    if (shouldFocusNextElement) {
      arrayOfRefs[nextIndex]?.focus();
    }
    // Call the selection function, if provided
    if (selectFunction) {
      selectFunction(nextIndex);
    }
  });
};
